import React, { useEffect, useMemo, useState } from "react";
import { times } from "lodash";

import {
  Box,
  Card,
  Chip,
  Typography,
  CardContent,
  TextField,
  Stack,
  Autocomplete,
  IconButton,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTheme } from "@mui/material/styles";
import { Percent, RestartAlt } from "@mui/icons-material";

import { useMap, MapboxGeoJSONFeature } from "react-map-gl";

import { HelpBox, NamedStatBox, MapSitesCard, SiteSearchCard } from "@/components/atoms";
import { extractBreaks, getFillColor, toPercentage } from "@/utils";
import { LocationResult, PenPortraitInsightVariable, SiteScore } from "@/types";

interface SiteInfo {name: string, latitude: number, longitude: number};

type OverlayProps = {
  siteData: SiteScore[];
  selectedSites: number[];
  scores: LocationResult;
  selectedGeo?: MapboxGeoJSONFeature;
  clearSelected: () => void;
  breaks: number[];
  firstTime: boolean;
  min: number;
  max: number;
  setSites: (sites: number[]) => void;
  siteInfo: SiteInfo | null;
  onSiteHover: (site_id: number | null) => void;
  onSiteDelete: (site_id: number) => void;
};

export const Overlay: React.FC<OverlayProps> = ({
                                                  siteData,
  selectedSites,
  scores,
  selectedGeo,
  clearSelected,
  breaks,
  firstTime,
  min,
  max,
  setSites,
  siteInfo,
  onSiteHover,
  onSiteDelete
}) => {
  const { current: map } = useMap();
  const theme = useTheme();

  const [siteType, setSiteType] = useState<string>("ALL");

  const filteredSites = useMemo(() => {
    const selectedSet = new Set(selectedSites);
    return siteData.filter((site) => selectedSet.has(site.site_id));
  }, [siteData, selectedSites]);

  const filteredSiteScores = useMemo(() => {
    return  selectedSites.length > 0 ? siteData?.filter(x => selectedSites.includes(x.tag_id)) || [] : [];
  }, [siteData, selectedSites])

  useEffect(() => {
    if (!firstTime) {
      map && map.isStyleLoaded() && resetMap();
      map &&
        scores.area.forEach((value) => {
          map.setFeatureState(
            {
              id: value.gid,
              source: "area-source",
              sourceLayer: "postal_area_test",
            },
            { score: value.rate, name: value.name, level: value.level },
          );
        });
      map &&
        map
          .getMap()
          .setPaintProperty(
            "area-base",
            "fill-color",
            getFillColor(extractBreaks(scores.area), theme.palette.gradient),
          );
      map &&
        scores.district.forEach((value) => {
          map.setFeatureState(
            {
              id: value.gid,
              source: "district-source",
              sourceLayer: "postal_district_test",
            },
            { score: value.rate, name: value.name, level: value.level },
          );
        });
      map &&
        map
          .getMap()
          .setPaintProperty(
            "district-base",
            "fill-color",
            getFillColor(extractBreaks(scores.district), theme.palette.gradient),
          );
      map &&
        scores.sector.forEach((value) => {
          map.setFeatureState(
            {
              id: value.gid,
              source: "sector-source",
              sourceLayer: "postal_sector_test",
            },
            { score: value.rate, name: value.name, level: value.level },
          );
        });
      map &&
        map
          .getMap()
          .setPaintProperty(
            "sector-base",
            "fill-color",
            getFillColor(extractBreaks(scores.sector), theme.palette.gradient),
          );
    }
  }, [firstTime, map, scores]);

  const resetMap = () => {
    clearSelected();
    map &&
      [...times(124)].forEach((value) => {
        map.setFeatureState(
          { id: value, source: "area-source", sourceLayer: "postal_area_test" },
          { score: null, name: null, level: null },
        );
      });
    map &&
      [...times(2839)].forEach((value) => {
        map.setFeatureState(
          {
            id: value,
            source: "district-source",
            sourceLayer: "postal_district_test",
          },
          { score: null, name: null, level: null },
        );
      });
    map &&
      [...times(9576)].forEach((value) => {
        map.setFeatureState(
          {
            id: value,
            source: "sector-source",
            sourceLayer: "postal_sector_test",
          },
          { score: null, name: null, level: null },
        );
      });
  };

  return (
    <Grid container size={12} spacing={2} height="100%" width="100%" p={2}>
      <Grid size={8}>

        {/* Geo Info */}
        {selectedGeo && (
          <Box
            sx={{
              backgroundColor: theme.palette.background.default,
              position: "absolute",
              bottom: 80,
              left: 12,
            }}
          >
            <NamedStatBox
              title={selectedGeo.state?.name || ""}
              name="Penetration"
              value={selectedGeo.state?.score || 0}
              valueFormatter={toPercentage}
              icon={<Percent fontSize="medium" color="secondary" />}
              description="The percentage of postcodes in this location that your audience covers"
            />
          </Box>
        )}

        {/* Gradient */}
        {breaks.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              bottom: 40,
              left: 12,
            }}
          >
            <Box
              sx={{
                width: "150px",
                height: "10px",
                borderRadius: "10px",
                background:
                  `linear-gradient(to right, ${theme.palette.gradient[0]}, ${theme.palette.gradient[1]}, ${theme.palette.gradient[3]}, ${theme.palette.gradient[4]})`,
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="caption">{toPercentage(0, 0)}</Typography>
              <Typography variant="caption">
                {toPercentage(breaks[0], 0)}
              </Typography>
              <Typography variant="caption">
                {toPercentage(breaks[1], 0)}
              </Typography>
              <Typography variant="caption">
                {toPercentage(breaks[2], 0)}
              </Typography>
              <Typography variant="caption">
                {toPercentage(breaks[3], 0)}
              </Typography>
            </Box>
          </Box>
        )}
      </Grid>


      <Grid size={4}>
        <>
          <MapSitesCard
            title={'Top Sites'}
            variables={filteredSiteScores}
            score={'score_wt'}
            min={min}
            max={max}
            siteInfo={siteInfo}
            onSiteHover={onSiteHover}
            onSiteDelete={onSiteDelete}
          />
          <SiteSearchCard
            siteData={siteData}
            filteredSites={filteredSites}
            setSites={setSites}
            selectedSites={selectedSites}
            siteType={siteType}
          />
        </>
      </Grid>
    </Grid>
  );
};
